import { useRoutes } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import queryString from 'query-string';
import { useEffect } from 'react';
import { useLocation, useRoute, useSearch } from 'wouter';

import { useSetPage } from '../contexts/PageContext';
import { useRequestBase } from '../contexts/RequestContext';
import useDocumentIsTrailer from './useDocumentIsTrailer';
import useOnClickLink from './useOnClickLink';

export { useTracking };

export function useDocumentPageViewTracking(shouldTrack, document: FullDocument) {
    const [location] = useLocation();
    const routes = useRoutes();
    const [micromagMatch = false, micromagParams] = useRoute(routes.micromag);
    const { path: screenParam = null } = micromagParams || {};
    const screenIndex = micromagMatch ? parseInt(screenParam || '1', 10) - 1 : null;
    const tracking = useTracking();
    const setPage = useSetPage();
    const isTrailer = useDocumentIsTrailer(document, location);
    useEffect(() => {
        if (shouldTrack) {
            tracking.trackPageViewFromDocument(document, {
                screenIndex,
                isTrailer,
            });
        }
    }, [shouldTrack, tracking, location, screenIndex]);
    useEffect(() => {
        if (document !== null) {
            setPage(tracking.getPageVariablesFromDocument(document));
        }
    }, [document]);
}

export function usePageViewTracking(shouldTrack, data = null) {
    const [location] = useLocation();
    const tracking = useTracking();
    const setPage = useSetPage();
    useEffect(() => {
        if (shouldTrack) {
            tracking.trackPageView(data);
        }
    }, [shouldTrack, location, tracking]);
    useEffect(() => {
        if (data !== null) {
            setPage(tracking.getPageVariables(data));
        }
    }, [data]);
}

export const useImagePixels = (pixels) => {
    const tracking = useTracking();
    const baseUri = useRequestBase();
    const searchQuery = useSearch();
    const [location] = useLocation();

    useEffect(() => {
        if (pixels !== null) {
            const urls = (pixels || []).map(({ url = null, params = null }) => {
                const urlParams = queryString.parse(searchQuery);
                const { fbclid = null } = urlParams || {};
                const paramsArray = params || [];
                const newQuery = {};

                if (paramsArray.indexOf('sourceUrl') !== -1) {
                    newQuery.sourceUrl = `${baseUri}${location}`;
                }

                if (paramsArray.indexOf('fbclid') !== -1 && fbclid !== null) {
                    newQuery.fbclid = fbclid;
                }

                // Try to get fbp from cookies?
                // if (paramsArray.indexOf('fbp') !== -1 && fbp !== null) {
                //     newQuery.fbc = fbp;
                // }

                return `${url}?${queryString.stringify(newQuery)}`;
            });
            urls.forEach((url) => {
                tracking.loadImagePixel(url);
            });
        }
    }, [
        tracking,
        baseUri,
        searchQuery,
        location,
        (pixels || [])
            .map((p) => p.url)
            .sort()
            .join(','),
    ]);
};

export const useTrackEvent = () => {
    const tracking = useTracking();
    const trackEvent = (category, action, label, value, data) => {
        if (category !== null && action !== null) {
            tracking.trackEvent(category, action, label, value, data);
        }
    };
    return trackEvent;
};

export const useTrackExperiment = (id = null) => {
    const tracking = useTracking();
    const trackExperiment = (action, variant, data) => {
        if (id !== null) {
            const variantKey = `experiments.${id}.variant`;
            tracking.trackEvent('Experiment', action, `${id}:${variant}`, null, {
                [variantKey]: action !== 'mark_done' ? variant : null,
                experiment: id,
                experimentVariant: variant,
                ...data,
            });
        }
    };
    return trackExperiment;
};

export const useTrackVideo = () => {
    const tracking = useTracking();
    const trackEvent = (action, video) => {
        tracking.trackVideo(action, video);
    };
    return trackEvent;
};

export const useTrackAd = () => {
    const tracking = useTracking();
    const trackEvent = (action, slot, renderEvent) => {
        tracking.trackAd(action, slot, renderEvent);
    };
    return trackEvent;
};

export const useTrackLink = () => {
    const tracking = useTracking();
    const trackEvent = (link, data) => {
        tracking.trackLink(link, data);
    };
    return trackEvent;
};

export const useTrackOnClickLink = (action = 'click', label = null) => {
    const tracking = useTracking();
    const onClick = ({ url }) => {
        tracking.trackEvent('Navigation', action, label || url);
    };
    return useOnClickLink(onClick);
};

export const useTrackOnClickCard = (action = 'click') => {
    const tracking = useTracking();
    const onClick = ({ url }, { title } = {}) => {
        tracking.trackEvent('Navigation', action, title || url);
    };
    return useOnClickLink(onClick);
};
